import React, { useEffect, useRef, useState, /*useContext*/ } from 'react'
import STYLES from "../../../constants/styles"
import Player from "@vimeo/player"
import { caseRef, caseCommentsRef } from '../../../firebase'
import {
  getDisplayThumbnailUrl,
  getThumbnailStatus,
  VIDEO_THUMBNAIL_STATUS,
} from '../../../utils/videoThumbnail'
// import { CircularProgress } from '@material-ui/core'
// import { GlobalContext } from '../../../hooks/reducer'
// https://www.npmjs.com/package/@vimeo/player#embed-options

const detailWidth = STYLES.share.detailWidth

export const Vimeo = (props) => {
  const { isWidthUp, videoId, thisCase, type } = props
  const uploadState = !!thisCase.videoUploaded
  const [uploaded, setUploaded] = useState(uploadState)
  const [loadError, setLoadError] = useState(false)
  const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false)
  const uploadedRef = useRef(uploadState)
  useEffect(() => {
    uploadedRef.current = uploaded
  }, [uploaded])
  // const { state: { shareCase }, /*dispatch*/ } = useContext(GlobalContext)
  // const { vimeoShow } = shareCase

  // NOTE: 動画を上寄せにする
  const percent = isWidthUp ? 44 : 84

  const iframeId = `vimeo-${videoId}`
  const caseKey = thisCase?.key
  const thumbnailStatus = getThumbnailStatus(thisCase?.video)
  const thumbnailUrl = getDisplayThumbnailUrl(thisCase?.video)

  useEffect(() => {
    // 別の投稿へ切り替わったら、遅延読み込み状態も初期化する
    uploadedRef.current = uploadState
    setUploaded(uploadState)
    setLoadError(false)
    setShouldLoadPlayer(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, caseKey, type])

  useEffect(() => {
    if (!shouldLoadPlayer) {
      return undefined
    }

    const options = {
      id: videoId,
      byline: false,
      portrait: false,
      title: false,
    }

    setLoadError(false)
    // propsから未アップロード状態に戻ったらローカルも初期化
    if (!uploadState) {
      uploadedRef.current = false
      setUploaded(false)
    }

    let player = null
    let timerId = null
    let errorTimer = null
    let cancelled = false
    const targetRef = type === 'caseComments' ? caseCommentsRef : caseRef

    const cleanupPlayer = () => {
      if (player) {
        player.destroy?.()
        player = null
      }
    }

    const cleanupTimer = () => {
      if (timerId) {
        clearInterval(timerId)
        timerId = null
      }
      if (errorTimer) {
        clearTimeout(errorTimer)
        errorTimer = null
      }
    }

    const markUploaded = () => {
      if (cancelled) return
      if (!uploadedRef.current) {
        uploadedRef.current = true
        setUploaded(true)
        if (caseKey) {
          targetRef.child(caseKey).update({ videoUploaded: true })
        }
      }
      setLoadError(false)
      cleanupTimer()
    }

    const markProcessing = () => {
      if (cancelled) return
      if (!uploadedRef.current) {
        setLoadError(false)
      }
    }

    const markError = () => {
      if (cancelled) return
      if (uploadedRef.current) {
        setLoadError(true)
        cleanupTimer()
      } else {
        setLoadError(false)
      }
    }

    // 進行中の再生を壊さない：loadVideoは使わない
    const checkVideo = () => {
      if (cancelled || !player) return
      // 軽い疎通確認に留める（失敗しても何もしない）
      player.getDuration().then((d) => {
        if (typeof d === 'number' && d > 0) {
          // console.log(`[Vimeo:${videoId}] ✅ markUploaded `)
          markUploaded()
        }
      }).catch(() => {/* noop */})
    }

    try {
      const container = document.getElementById(iframeId)
      if (container) {
        container.innerHTML = ''
      }
      player = new Player(iframeId, options)

      player.ready().then(() => {
        if (cancelled) return
        // console.log(`[Vimeo:${videoId}] ✅ player.ready() 完了`)
        checkVideo()
      }).catch((error) => {
        if (cancelled) return
        console.log('Vimeo ready error', error, videoId)
        markProcessing()
      })

      // 再生できたら勝ち：ここでuploaded確定＆エラー消去
      let playedOnce = false
      const onBecameHealthy = () => {
        if (cancelled) return
        playedOnce = true
        // console.log(`[Vimeo:${videoId}] ▶️ onBecameHealthy 発火（再生開始 or timeupdate）`)
        markUploaded()
        setLoadError(false)
      }
      player.on('playing', onBecameHealthy)
      player.on('timeupdate', (data) => {
        if (!playedOnce && data?.seconds > 0.5) {
          // console.log(`[Vimeo:${videoId}] ⏱ timeupdate: ${data.seconds.toFixed(2)}s`)
          onBecameHealthy()
        }
      })

      player.on('error', (error) => {
        if (cancelled) return
        // console.log(`[Vimeo:${videoId}] ❌ error発生`, error)
        // 2秒様子を見て、回復してなければエラー表示
        if (errorTimer) clearTimeout(errorTimer)
        errorTimer = setTimeout(() => {
          // console.log(`[Vimeo:${videoId}] ❌ error継続中(2秒経過) → loadError=${!uploadedRef.current}`)
          if (!uploadedRef.current) { // 未アップロード扱い時のみ表示
            setLoadError(true)
          }
        }, 2000)
      })

    } catch (err) {
      console.log('Failed to initialise Vimeo player', err, videoId)
      markError()
    }

    // 軽いポーリングはしてもOKだが、loadVideoは呼ばない・短期間のみ
    if (!uploadedRef.current) {
      const started = Date.now()
      timerId = setInterval(() => {
        // console.log(`[Vimeo:${videoId}] ⏹ ポーリング終了(60秒経過)`)
        if (Date.now() - started > 60_000) { clearInterval(timerId); return } // 1分で打ち切り
        // console.log(`[Vimeo:${videoId}] 🔁 checkVideo() 実行`)
        checkVideo()
      }, 10_000)
    }

    return () => {
      cancelled = true
      cleanupTimer()
      cleanupPlayer()
      const container = document.getElementById(iframeId)
      if (container) {
        container.innerHTML = ''
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadPlayer, videoId, type, caseKey])

  const baseStyles = {
    width: isWidthUp ? ((100 - detailWidth) + '%') : '100%',
    paddingBottom: `calc(var(--video--height) / var(--video--width) * ${percent}%)`,
  }
  const cmntStyles = {
    width: '90%',
    marginLeft: '10%',
  }
  const isThumbnailPending = thumbnailStatus === VIDEO_THUMBNAIL_STATUS.pending
  const isThumbnailFallback = thumbnailStatus === VIDEO_THUMBNAIL_STATUS.fallback

  return <React.Fragment>
  <div
    className="embed-container"
    style={type === 'caseComments' ? cmntStyles : baseStyles}
  >
    {shouldLoadPlayer ? (
      <div id={iframeId} key={videoId}></div>
    ) : (
      // 一覧初期表示では Vimeo Player を作らず、明示操作時だけ読み込む
      <button
        type="button"
        aria-label="クリックして動画を読み込む"
        onClick={() => setShouldLoadPlayer(true)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
          padding: 0,
          backgroundColor: '#111',
          cursor: 'pointer',
        }}
      >
        <img
          src={thumbnailUrl}
          alt="動画サムネイル"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.88,
          }}
        />
        {(isThumbnailPending || isThumbnailFallback) && (
          <span
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              padding: '3px 5px',
              borderRadius: '999px',
              color: '#fff',
              fontSize: '0.75em',
            }}
          >
            サムネイル準備中
          </span>
        )}
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '10px 16px',
            borderRadius: '999px',
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
            color: '#fff',
            fontSize: '0.9em',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}
        >
          クリックして動画を読み込む
        </span>
      </button>
    )}

    {shouldLoadPlayer && (((!uploaded && !loadError) || loadError)) ? (
      <div style={{
        textAlign: 'center',
        color: '#555',
        pointerEvents: 'none',
        fontSize: '0.8em',
      }}>
        {loadError
          ? '動画を取得できませんでした'
          : (
            <>動画を処理しています。<br />
            数分後に再度アクセスしてください。
            </>
          )}
      </div>
    ) : null}

    <div
      style={{
        textAlign: 'center',
        position: 'absolute',
        width: '100%',
        left: `50%`,
        bottom: '-20px',
        transform: `translateX(-50%)`,
        fontSize: '0.8em',
      }}
    >
      ※アップロード処理・読み込みに時間がかかることがあります
    </div>
  </div>
  </React.Fragment>
}
